/**
 * Service Worker e verificação de versão para cache invalidation
 * 
 * Este módulo:
 * 1. Verifica se há uma nova versão do app
 * 2. Força atualização quando chunks antigos não são encontrados
 * 3. Notifica o usuário sobre atualizações disponíveis
 */

declare const __APP_VERSION__: string;

const VERSION_KEY = 'app_version';
const RELOAD_FLAG = 'app_needs_reload';

/**
 * Obtém a versão atual do app
 */
export function getAppVersion(): string {
  return typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : 'unknown';
}

/**
 * Verifica se há uma nova versão e se precisa recarregar
 */
export function checkVersion(): boolean {
  const storedVersion = localStorage.getItem(VERSION_KEY);
  const currentVersion = getAppVersion();
  
  if (storedVersion && storedVersion !== currentVersion) {
    console.log(`[VersionCheck] Nova versão detectada: ${storedVersion} -> ${currentVersion}`);
    localStorage.setItem(VERSION_KEY, currentVersion);
    return true; // Nova versão disponível
  }
  
  localStorage.setItem(VERSION_KEY, currentVersion);
  return false;
}

/**
 * Handler global para erros de import dinâmico (chunk não encontrado)
 * Força reload quando um chunk antigo não é encontrado no servidor
 */
export function setupChunkErrorHandler(): void {
  const originalOnError = window.onerror;
  
  window.onerror = function(message, source, lineno, colno, error) {
    // Detecta erros de chunk não encontrado
    if (
      error?.message?.includes('Failed to fetch dynamically imported module') ||
      error?.message?.includes('Loading chunk') ||
      error?.message?.includes('net::ERR_ABORTED 404')
    ) {
      console.error('[VersionCheck] Chunk não encontrado, forçando atualização...');
      handleChunkLoadError();
      return true; // Previne propagação do erro
    }
    
    // Chama o handler original se existir
    if (originalOnError) {
      return originalOnError.call(window, message, source, lineno, colno, error);
    }
    return false;
  };
  
  // Handler para unhandled promise rejections (import() falha como promise)
  window.addEventListener('unhandledrejection', (event) => {
    const error = event.reason;
    if (
      error?.message?.includes('Failed to fetch dynamically imported module') ||
      error?.message?.includes('Loading chunk') ||
      String(error).includes('404')
    ) {
      console.error('[VersionCheck] Chunk não encontrado (promise), forçando atualização...');
      event.preventDefault();
      handleChunkLoadError();
    }
  });
}

/**
 * Trata erro de chunk não encontrado
 */
function handleChunkLoadError(): void {
  // Evita loops infinitos de reload
  const reloadAttempts = parseInt(sessionStorage.getItem('chunk_reload_attempts') || '0', 10);
  
  if (reloadAttempts >= 2) {
    console.error('[VersionCheck] Máximo de tentativas de reload atingido. Limpe o cache manualmente.');
    sessionStorage.removeItem('chunk_reload_attempts');
    
    // Mostra mensagem para o usuário
    showUpdateNotification(true);
    return;
  }
  
  sessionStorage.setItem('chunk_reload_attempts', String(reloadAttempts + 1));
  
  // Limpa caches do navegador se disponível
  const reloadPage = () => {
    (window as Window).location.reload();
  };
  
  if ('caches' in window && window.caches) {
    window.caches.keys().then((names) => {
      names.forEach((name) => {
        window.caches?.delete(name);
      });
    }).finally(reloadPage);
  } else {
    // Fallback: reload simples
    reloadPage();
  }
}

/**
 * Mostra notificação de atualização disponível
 */
export function showUpdateNotification(forceRefresh = false): void {
  // Verifica se já existe notificação
  if (document.getElementById('version-update-banner')) {
    return;
  }
  
  const banner = document.createElement('div');
  banner.id = 'version-update-banner';
  banner.style.cssText = `
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    background: linear-gradient(90deg, #3b82f6, #2563eb);
    color: white;
    padding: 12px 20px;
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 16px;
    z-index: 99999;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    font-size: 14px;
    box-shadow: 0 2px 8px rgba(0,0,0,0.15);
  `;
  
  const message = forceRefresh
    ? '⚠️ Erro ao carregar recursos. Por favor, limpe o cache do navegador (Ctrl+Shift+R).'
    : '🚀 Uma nova versão está disponível!';
  
  banner.innerHTML = `
    <span>${message}</span>
    <button id="version-update-btn" style="
      background: white;
      color: #2563eb;
      border: none;
      padding: 8px 16px;
      border-radius: 6px;
      cursor: pointer;
      font-weight: 600;
      transition: transform 0.1s;
    ">
      ${forceRefresh ? 'Recarregar Página' : 'Atualizar Agora'}
    </button>
    <button id="version-dismiss-btn" style="
      background: transparent;
      color: white;
      border: 1px solid rgba(255,255,255,0.5);
      padding: 8px 12px;
      border-radius: 6px;
      cursor: pointer;
      font-weight: 500;
    ">
      Depois
    </button>
  `;
  
  document.body.prepend(banner);
  
  document.getElementById('version-update-btn')?.addEventListener('click', () => {
    // Limpa caches e recarrega
    const reloadPage = () => {
      (window as Window).location.reload();
    };
    
    if ('caches' in window && window.caches) {
      window.caches.keys().then((names) => {
        Promise.all(names.map(name => window.caches?.delete(name))).then(reloadPage);
      });
    } else {
      reloadPage();
    }
  });
  
  document.getElementById('version-dismiss-btn')?.addEventListener('click', () => {
    banner.remove();
  });
}

/**
 * Inicializa o sistema de verificação de versão
 */
export function initVersionCheck(): void {
  // Configura handler para erros de chunk
  setupChunkErrorHandler();
  
  // Verifica versão na inicialização
  const hasNewVersion = checkVersion();
  
  if (hasNewVersion) {
    // Pequeno delay para não atrapalhar o carregamento inicial
    setTimeout(() => {
      showUpdateNotification();
    }, 2000);
  }
  
  // Limpa flag de reload bem-sucedido
  sessionStorage.removeItem('chunk_reload_attempts');
  
  console.log(`[VersionCheck] App version: ${getAppVersion()}`);
}
